import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

const HeroSection = () => {
  const { t } = useLanguage();

  // Smooth scroll handler
  const handleScrollTo = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      className="w-full bg-white dark:bg-[#051518] px-4 md:px-6 pt-5 pb-6 flex items-center justify-center transition-colors duration-300"
    >
      {/* Framed Card Container with Rounded Outer Edges */}
      <div className="relative w-full max-w-[1520px] min-h-[85vh] flex items-center justify-center overflow-hidden rounded-[32px] border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.25)] bg-[#0A2E36] bg-[url('https://cdn.prod.website-files.com/6a29d43f03b10014aa9b0c8c/6a2bdaba88829c3cbf499915_23257865fdb770c66125ec380f10c7ef8c0c6dbd%20(3).webp')] bg-cover bg-center bg-no-repeat text-white">

        {/* Background Video */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover opacity-45"
            poster="/dental_hero_banner.jpg"
          >
            <source
              src="/bg-video.mp4"
              type="video/mp4"
            />
          </video>
          {/* Soft dark teal gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A2E36]/95 via-[#0A2E36]/80 to-[#0A2E36]/40"></div>
        </div>

        {/* Main Grid Container */}
        <div className="relative z-10 w-full px-6 md:px-8 lg:px-12 pt-28 pb-12 md:pt-36 md:pb-16 lg:pt-40 lg:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">

          {/* Left Content Column */}
          <div className="lg:col-span-7 flex flex-col justify-center space-y-6 md:space-y-8 animate-[fadeIn_1s_ease-out]">
            {/* Top Badge */}
            <div className="inline-flex items-center space-x-2">
              <span className="h-[2px] w-8 bg-[#E09F3E]"></span>
              <span className="text-[#E09F3E] text-xs font-bold uppercase tracking-[0.2em]">
                {t.hero.subtitle}
              </span>
            </div>

            {/* Heading - Serif Font to match Premier Arts */}
            <h1 className="text-4xl md:text-5xl lg:text-[56px] font-serif font-light leading-tight tracking-tight text-white">
              {t.hero.title}
            </h1>

            {/* Description */}
            <p className="text-white/80 text-base md:text-lg leading-relaxed max-w-2xl">
              {t.hero.desc}
            </p>

            {/* Call to Actions - Pill Shaped Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 pt-2">
              <Link
                to="/contact"
                className="px-8 py-4 bg-[#56b255] hover:bg-[#469e45] text-white text-center font-bold rounded-full shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center space-x-2 border border-transparent"
              >
                {/* Calendar Icon */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span>{t.hero.bookButton}</span>
              </Link>

              <a
                href="tel:+916281785274"
                className="px-8 py-4 bg-transparent hover:bg-white/10 text-white text-center font-bold rounded-full border-2 border-white/30 hover:border-white transition-all duration-300"
              >
                {t.hero.callButton} — +91 6281785274
              </a>
            </div>

            {/* Bottom Cards Info Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-6 border-t border-white/10">
              {/* Card 1: Patient Education */}
              <div className="bg-[#0F4C5C]/20 border border-white/10 hover:border-white/20 p-5 rounded-[20px] flex items-start space-x-4 transition-all duration-300 backdrop-blur-md group hover:bg-[#0F4C5C]/35">
                <div className="p-2.5 bg-white/10 border border-white/10 text-white rounded-xl group-hover:bg-white/15 transition-colors">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-white text-base">{t.hero.card1Title}</h4>
                  <p className="text-sm text-white/70 leading-relaxed mt-1">
                    {t.hero.card1Desc}
                  </p>
                  <button
                    onClick={() => handleScrollTo("services")}
                    className="text-xs font-bold text-white hover:text-[#E09F3E] flex items-center space-x-1 mt-3 transition-colors"
                  >
                    <span>{t.hero.card1Button}</span>
                  </button>
                </div>
              </div>

              {/* Card 2: Patient Reviews */}
              <div className="bg-[#0F4C5C]/20 border border-white/10 hover:border-white/20 p-5 rounded-[20px] flex items-start space-x-4 transition-all duration-300 backdrop-blur-md group hover:bg-[#0F4C5C]/35">
                <div className="p-2.5 bg-white/10 border border-white/10 text-white rounded-xl group-hover:bg-white/15 transition-colors">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-white text-base">{t.hero.card2Title}</h4>
                  <p className="text-sm text-white/70 leading-relaxed mt-1">
                    {t.hero.card2Desc}
                  </p>
                  <button
                    onClick={() => handleScrollTo("reviews")}
                    className="text-xs font-bold text-white hover:text-[#E09F3E] flex items-center space-x-1 mt-3 transition-colors"
                  >
                    <span>{t.hero.card2Button}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content Column - Scrolling Masonry Grid of Clinic Photos */}
          <div className="lg:col-span-5 grid grid-cols-2 gap-4 h-[650px] overflow-hidden relative z-10">
            {/* Column 1 - Left (Scrolls Up) */}
            <div className="flex flex-col space-y-4 animate-scroll-up">
              <div className="overflow-hidden rounded-[24px] border border-white/10 shadow-lg bg-[#0F4C5C]/20 aspect-[4/3] w-full">
                <img
                  src="/clinic/waiting_area_reception_desk_and_chairs.png"
                  alt="Dr. Krishna's Dental Reception & Waiting Area"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="overflow-hidden rounded-[24px] border border-white/10 shadow-lg bg-[#0F4C5C]/20 aspect-[3/4] w-full">
                <img
                  src="/clinic/patient_treatment_chair.png"
                  alt="Dr. Krishna's Dental Treatment Chair"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="overflow-hidden rounded-[24px] border border-white/10 shadow-lg bg-[#0F4C5C]/20 aspect-[4/3] w-full">
                <img
                  src="/clinic/doctors_room.jpg"
                  alt="Dr. Krishna's Dental Doctor's Consultation Room"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="overflow-hidden rounded-[24px] border border-white/10 shadow-lg bg-[#0F4C5C]/20 aspect-[4/3] w-full">
                <img
                  src="/clinic/outside_daytime.jpg"
                  alt="Dr. Krishna's Dental Clinic Outside Signage"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              {/* Duplicated for seamless infinite scrolling */}
              <div className="overflow-hidden rounded-[24px] border border-white/10 shadow-lg bg-[#0F4C5C]/20 aspect-[4/3] w-full">
                <img
                  src="/clinic/waiting_area_reception_desk_and_chairs.png"
                  alt="Dr. Krishna's Dental Reception & Waiting Area"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="overflow-hidden rounded-[24px] border border-white/10 shadow-lg bg-[#0F4C5C]/20 aspect-[3/4] w-full">
                <img
                  src="/clinic/patient_treatment_chair.png"
                  alt="Dr. Krishna's Dental Treatment Chair"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="overflow-hidden rounded-[24px] border border-white/10 shadow-lg bg-[#0F4C5C]/20 aspect-[4/3] w-full">
                <img
                  src="/clinic/doctors_room.jpg"
                  alt="Dr. Krishna's Dental Doctor's Consultation Room"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="overflow-hidden rounded-[24px] border border-white/10 shadow-lg bg-[#0F4C5C]/20 aspect-[4/3] w-full">
                <img
                  src="/clinic/outside_daytime.jpg"
                  alt="Dr. Krishna's Dental Clinic Outside Signage"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>

            {/* Column 2 - Right (Scrolls Down) */}
            <div className="flex flex-col space-y-4 animate-scroll-down">
              <div className="overflow-hidden rounded-[24px] border border-white/10 shadow-lg bg-[#0F4C5C]/20 aspect-[3/4] w-full">
                <img
                  src="/clinic/clinic_rooms_outside_view.png"
                  alt="Dr. Krishna's Dental Clinical Corridor"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="overflow-hidden rounded-[24px] border border-white/10 shadow-lg bg-[#0F4C5C]/20 aspect-[4/3] w-full">
                <img
                  src="/clinic/outside_view_enhanced.jpg"
                  alt="Dr. Krishna's Dental Exterior Front View"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="overflow-hidden rounded-[24px] border border-white/10 shadow-lg bg-[#0F4C5C]/20 aspect-[3/4] w-full">
                <img
                  src="/clinic/patient_treatment_chairs.png"
                  alt="Dr. Krishna's Dental Clinical Operatory Setup"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="overflow-hidden rounded-[24px] border border-white/10 shadow-lg bg-[#0F4C5C]/20 aspect-[4/3] w-full">
                <img
                  src="/clinic/waiting_area_reception_desk_and_chairs_2.png"
                  alt="Dr. Krishna's Dental Waiting Lounge"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              {/* Duplicated for seamless infinite scrolling */}
              <div className="overflow-hidden rounded-[24px] border border-white/10 shadow-lg bg-[#0F4C5C]/20 aspect-[3/4] w-full">
                <img
                  src="/clinic/clinic_rooms_outside_view.png"
                  alt="Dr. Krishna's Dental Clinical Corridor"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="overflow-hidden rounded-[24px] border border-white/10 shadow-lg bg-[#0F4C5C]/20 aspect-[4/3] w-full">
                <img
                  src="/clinic/outside_view_enhanced.jpg"
                  alt="Dr. Krishna's Dental Exterior Front View"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="overflow-hidden rounded-[24px] border border-white/10 shadow-lg bg-[#0F4C5C]/20 aspect-[3/4] w-full">
                <img
                  src="/clinic/patient_treatment_chairs.png"
                  alt="Dr. Krishna's Dental Clinical Operatory Setup"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="overflow-hidden rounded-[24px] border border-white/10 shadow-lg bg-[#0F4C5C]/20 aspect-[4/3] w-full">
                <img
                  src="/clinic/waiting_area_reception_desk_and_chairs_2.png"
                  alt="Dr. Krishna's Dental Waiting Lounge"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default HeroSection;
